import { Injectable, inject, Signal, WritableSignal, signal, computed } from '@angular/core';
import { ITimeZone } from '../api/mylb/interfaces/itime-zone.interface';
import { IUser } from '../api/mylb/interfaces/iuser.interface';
import { GlobalTimezoneService } from './global-timezone.service';
import { convertTimeBetweenTimezones, convertDayBetweenTimezones, getDSTAwareOffset } from '../functions/timezone-utils';
import { convertUtcToLocalAvailability } from '../functions/availability-conversions.function';
import { timezonesAreFunctionallyEquivalent } from '../functions/browser-timezone.function';
import { AccountStateService } from '@lb/state/account-state.service';
import { TimezoneStateService } from '@lb/state/timezone-state.service';

export interface AvailabilityTimezoneContext {
	sourceTimezone?: ITimeZone | null;
	displayTimezone?: ITimeZone | null;
	instructorTimezone?: ITimeZone | null;
	showTimezoneNote: boolean;
	isConverted: boolean;
	originalTimes: { [dayIndex: number]: { startTime: string; endTime: string; timezoneAbbr: string } };
}

export interface TimeRange {
	dayIndex: number;
	startTime: string;
	endTime: string;
	dayName?: string;
}

export interface ConvertedRange {
	dayIndex: number;
	startTime: string;
	endTime: string;
	dayName: string;
}

export interface ConversionResult {
	success: boolean;
	ranges: any[];
	errors: ConversionError[];
	warnings: ConversionWarning[];
}

export interface MergeResult {
	success: boolean;
	mergedRanges: any[];
	conflicts: MergeConflict[];
	mergedCount: number;
}

export interface ConversionError {
	type: 'day_boundary_crossing' | 'dst_transition' | 'invalid_time' | 'timezone_missing';
	message: string;
	originalRange?: any;
	affectedRange?: any;
}

export interface ConversionWarning {
	type: 'time_adjustment' | 'day_shift' | 'merge_required';
	message: string;
	originalRange?: any;
	adjustedRange?: any;
}

export interface MergeConflict {
	type: 'overlap' | 'gap' | 'different_blocks';
	message: string;
	conflictingRanges: any[];
}

export interface Range {
	dayIndex: number;
	startTime: string;
	endTime: string;
	instructorAvailabilityBlockID?: number;
	timezoneID?: number;
}

@Injectable({
	providedIn: 'root'
})
export class AvailabilityTimezoneService {
	private _globalTimezoneService = inject(GlobalTimezoneService);
	private _timezoneStateService = inject(TimezoneStateService);
	private _accountStateService = inject(AccountStateService);

	private _availableTimezones: Signal<ITimeZone[]> = this._timezoneStateService.timezones;
	private _account: Signal<IUser> = this._accountStateService.user;

	// Current timezone context for the availability operation
	private _currentTimezoneContext: WritableSignal<AvailabilityTimezoneContext> = signal({
		sourceTimezone: undefined,
		displayTimezone: undefined,
		instructorTimezone: undefined,
		showTimezoneNote: false,
		isConverted: false,
		originalTimes: {}
	});

	// Public getter for timezone context
	public timezoneContext: Signal<AvailabilityTimezoneContext> = this._currentTimezoneContext.asReadonly();

	// Public getters using global timezone service
	public displayTimezone: Signal<ITimeZone | null> = this._globalTimezoneService.effectiveTimezone;
	public instructorTimezone: Signal<ITimeZone | null> = computed(() => this._account()?.timeZone || null);
	public browserTimezone: Signal<ITimeZone | null> = this._globalTimezoneService.browserTimezone;

	/**
	 * Converts raw availability data from instructor timezone to display timezone
	 * Handles day splitting when timezone conversion causes ranges to span multiple days
	 */
	public convertAvailabilityToDisplayTimezone(rawData: any[]): any[] {
		if (!rawData?.length) {
			return rawData || [];
		}

		const displayTz = this.displayTimezone();
		const instructorTz = this.instructorTimezone();
		const availableTimezones = this._availableTimezones();

		if (!displayTz || !instructorTz || !availableTimezones?.length) {
			return rawData;
		}

		// Check if this is timezone-aware data (has timezoneID)
		const isTimezoneAware = rawData.some((item) => item.timezoneID);

		if (!isTimezoneAware) {
			// Fall back to legacy conversion for non-timezone-aware data
			return convertUtcToLocalAvailability(rawData, displayTz, availableTimezones);
		}

		// For timezone-aware data, handle conversion with proper day splitting
		const convertedRanges: any[] = [];

		rawData.forEach((availability) => {
			const sourceTimezone = availableTimezones.find((tz) => tz.timeZoneID === availability.timezoneID) || instructorTz;

			// Skip conversion if timezones are the same
			if (sourceTimezone.timeZoneID === displayTz.timeZoneID) {
				convertedRanges.push(availability);
				return;
			}

			// Convert times using timezone utilities
			const convertedStartTime = convertTimeBetweenTimezones(availability.startTime, sourceTimezone, displayTz);
			const convertedEndTime = convertTimeBetweenTimezones(availability.endTime, sourceTimezone, displayTz);

			// Check if conversion causes day changes
			const originalDayIndex = availability.dayIndex;
			const startDayIndex = convertDayBetweenTimezones(originalDayIndex, availability.startTime, sourceTimezone, displayTz);
			const endDayIndex = convertDayBetweenTimezones(originalDayIndex, availability.endTime, sourceTimezone, displayTz);

			if (startDayIndex === endDayIndex) {
				// No day crossing - simple conversion
				convertedRanges.push({
					...availability,
					startTime: convertedStartTime,
					endTime: convertedEndTime,
					dayIndex: startDayIndex
				});
			} else {
				// Day crossing - split into two ranges

				// First range: from converted start time to end of start day (23:59)
				convertedRanges.push({
					...availability,
					startTime: convertedStartTime,
					endTime: '23:59',
					dayIndex: startDayIndex
				});

				// Second range: from start of end day (00:00) to converted end time
				convertedRanges.push({
					...availability,
					startTime: '00:00',
					endTime: convertedEndTime,
					dayIndex: endDayIndex
				});
			}
		});

		// Merge ranges that are touching within the same day only
		// This will NOT merge timezone-split ranges across different days
		const mergedRanges = this.mergeAdjacentDayRanges(convertedRanges);

		return mergedRanges;
	}

	/**
	 * Sets up timezone context for an availability operation
	 */
	public setupTimezoneContext(availabilityData: any[]): void {
		const instructorTz = this.instructorTimezone();
		const displayTz = this.displayTimezone();
		const availableTimezones = this._availableTimezones();

		// Get the actual instructor's timezone from the availability data if available
		const actualInstanceTz =
			availabilityData?.length > 0 ? availableTimezones.find((tz) => tz.timeZoneID === availabilityData[0]?.timezoneID) || instructorTz : instructorTz;

		const sourceTimezone = actualInstanceTz;

		// Determine if we need timezone conversion and context messaging
		const needsConversion = displayTz && sourceTimezone && displayTz.timeZoneID !== sourceTimezone.timeZoneID;

		// Check if the display timezone is functionally equivalent to the source timezone
		// This handles cases where timezones have the same offset/abbreviation but different names
		const displayIsEquivalentToSource = displayTz && sourceTimezone && this.areTimezonesEquivalent(displayTz, sourceTimezone);

		// Only show timezone note if there's actual meaningful conversion happening
		// Hide the banner if timezones are functionally equivalent (same offset/abbreviation)
		const showTimezoneNote = needsConversion && !displayIsEquivalentToSource;

		// Update timezone context
		this._currentTimezoneContext.set({
			sourceTimezone,
			displayTimezone: displayTz,
			instructorTimezone: actualInstanceTz,
			showTimezoneNote: !!showTimezoneNote,
			isConverted: !!needsConversion,
			originalTimes: {}
		});
	}

	/**
	 * Converts form time ranges from display timezone back to instructor timezone for saving
	 * For instructor availability, we preserve the original day selection and only convert times
	 */
	public convertTimeRangesForSaving(timeRanges: TimeRange[]): TimeRange[] {
		const displayTz = this.displayTimezone();
		const instructorTz = this.timezoneContext().instructorTimezone;
		const needsConversion = displayTz && instructorTz && displayTz.timeZoneID !== instructorTz.timeZoneID;

		if (!needsConversion || !displayTz || !instructorTz) {
			return timeRanges;
		}

		const result = timeRanges.map((range) => {
			let startTime = range.startTime;
			let endTime = range.endTime;
			let dayIndex = range.dayIndex;

			// Convert back to instructor timezone
			const convertedStartTime = convertTimeBetweenTimezones(startTime, displayTz, instructorTz);
			const convertedEndTime = convertTimeBetweenTimezones(endTime, displayTz, instructorTz);

			// For instructor availability saving, preserve the day selection in most cases
			// Only change the day if the time conversion creates a major shift (> 12 hours)
			// This preserves user intent when they select "Tuesday" - it should stay Tuesday

			const originalStartHour = parseInt(startTime.split(':')[0]);
			const convertedStartHour = parseInt(convertedStartTime.split(':')[0]);
			const hourShift = Math.abs(convertedStartHour - originalStartHour);

			// Only adjust day if there's a major time shift that suggests day boundary crossing
			let finalDayIndex = dayIndex;
			if (hourShift > 12) {
				// Major shift detected - likely crossed day boundary
				if (originalStartHour > 12 && convertedStartHour < 12) {
					// Went from PM to AM - likely next day
					finalDayIndex = (dayIndex + 1) % 7;
				} else if (originalStartHour < 12 && convertedStartHour > 12) {
					// Went from AM to PM - likely previous day
					finalDayIndex = (dayIndex + 6) % 7;
				}
			}

			const convertedRange = {
				dayIndex: finalDayIndex,
				startTime: convertedStartTime,
				endTime: convertedEndTime
			};

			return convertedRange;
		});

		return result;
	}

	/**
	 * Processes converted availability ranges for form display
	 */
	public processConvertedRangesForForm(availabilityBlock: any[]): ConvertedRange[] {
		if (!availabilityBlock?.length) {
			return [];
		}

		let convertedRanges: ConvertedRange[] = [];

		availabilityBlock.forEach((availability: any) => {
			// The service has already converted times to display timezone and handled day splitting
			// We just need to round the times and format them for the form
			const startTime = availability.startTime;
			const endTime = availability.endTime;
			const dayIndex = availability.dayIndex;

			// Round times to 30-minute intervals for form display
			// Pass isEndTime parameter to handle start vs end time rounding differently
			const roundedStartTime = this.roundTimeToInterval(startTime, 30, false); // Start time
			const roundedEndTime = this.roundTimeToInterval(endTime, 30, true); // End time

			const dayName = this.getDayName(dayIndex);
			const range = {
				dayIndex,
				startTime: roundedStartTime,
				endTime: roundedEndTime,
				dayName
			};

			convertedRanges.push(range);
		});

		// Merge adjacent time ranges to create a cleaner display
		const mergedRanges = this.mergeTimeRanges(convertedRanges);

		return mergedRanges;
	}

	/**
	 * Gets appropriate timezone message for UI banners
	 */
	public getTimezoneMessage(): string {
		const context = this.timezoneContext();
		const displayTz = context.displayTimezone;
		const instructorTz = context.instructorTimezone;
		const isConverted = context.isConverted;

		if (!displayTz || !instructorTz) {
			return 'Times are displayed in instructor timezone';
		}

		if (isConverted) {
			return `Times shown in ${displayTz.abbreviation} but will be saved in instructor timezone (${instructorTz.abbreviation})`;
		} else {
			return `Times displayed in ${displayTz.abbreviation} timezone`;
		}
	}

	/**
	 * Enhanced conversion with comprehensive validation and error handling
	 */
	public convertAvailabilityWithValidation(data: any[]): ConversionResult {
		const result: ConversionResult = {
			success: true,
			ranges: [],
			errors: [],
			warnings: []
		};

		if (!data?.length) {
			return result;
		}

		const displayTz = this.displayTimezone();
		const instructorTz = this.instructorTimezone();
		const availableTimezones = this._availableTimezones();

		// Validate prerequisite conditions
		if (!displayTz) {
			result.errors.push({
				type: 'timezone_missing',
				message: 'Display timezone is not available'
			});
			result.success = false;
			return result;
		}

		if (!instructorTz) {
			result.errors.push({
				type: 'timezone_missing',
				message: 'Instructor timezone is not available'
			});
			result.success = false;
			return result;
		}

		if (!availableTimezones?.length) {
			result.errors.push({
				type: 'timezone_missing',
				message: 'Timezone data is not available'
			});
			result.success = false;
			return result;
		}

		// Process each availability range
		data.forEach((availability, index) => {
			try {
				const conversionResult = this.convertSingleRangeWithValidation(availability, displayTz, instructorTz, availableTimezones, index);

				result.ranges.push(...conversionResult.ranges);
				result.errors.push(...conversionResult.errors);
				result.warnings.push(...conversionResult.warnings);

				if (!conversionResult.success) {
					result.success = false;
				}
			} catch (error) {
				result.errors.push({
					type: 'invalid_time',
					message: `Failed to process availability range ${index}: ${error}`,
					originalRange: availability
				});
				result.success = false;
			}
		});

		// Apply range merging with conflict detection
		if (result.ranges.length > 0) {
			const mergeResult = this.mergeRangesWithConflictDetection(result.ranges);
			result.ranges = mergeResult.mergedRanges;

			// Add merge conflicts as warnings
			mergeResult.conflicts.forEach((conflict) => {
				result.warnings.push({
					type: 'merge_required',
					message: conflict.message
				});
			});

			if (mergeResult.mergedCount > 0) {
				result.warnings.push({
					type: 'merge_required',
					message: `Merged ${mergeResult.mergedCount} adjacent time ranges`
				});
			}
		}

		return result;
	}

	/**
	 * Enhanced range merging with conflict detection
	 */
	public mergeRangesWithConflictDetection(ranges: any[]): MergeResult {
		const result: MergeResult = {
			success: true,
			mergedRanges: [],
			conflicts: [],
			mergedCount: 0
		};

		if (!ranges?.length) {
			return result;
		}

		// Group ranges by day and block ID for proper merging
		const grouped = this.groupRangesForMerging(ranges);

		Object.values(grouped).forEach((group) => {
			const mergeGroupResult = this.mergeRangeGroup(group);
			result.mergedRanges.push(...mergeGroupResult.ranges);
			result.conflicts.push(...mergeGroupResult.conflicts);
			result.mergedCount += mergeGroupResult.mergedCount;
		});

		return result;
	}

	/**
	 * Enhanced saving conversion with day preservation logic
	 */
	public convertForSavingWithDayPreservation(ranges: TimeRange[]): ConversionResult {
		const result: ConversionResult = {
			success: true,
			ranges: [],
			errors: [],
			warnings: []
		};

		if (!ranges?.length) {
			return result;
		}

		const displayTz = this.displayTimezone();
		const instructorTz = this.timezoneContext().instructorTimezone;

		if (!displayTz || !instructorTz) {
			result.errors.push({
				type: 'timezone_missing',
				message: 'Required timezone information is missing'
			});
			result.success = false;
			return result;
		}

		const needsConversion = displayTz.timeZoneID !== instructorTz.timeZoneID;

		if (!needsConversion) {
			result.ranges = ranges.map((range) => ({ ...range }));
			return result;
		}

		// Process each range with day preservation logic
		ranges.forEach((range, index) => {
			try {
				const conversionResult = this.convertRangeForSavingWithPreservation(range, displayTz, instructorTz, index);

				result.ranges.push(conversionResult.range);
				result.errors.push(...conversionResult.errors);
				result.warnings.push(...conversionResult.warnings);

				if (!conversionResult.success) {
					result.success = false;
				}
			} catch (error) {
				result.errors.push({
					type: 'invalid_time',
					message: `Failed to convert range ${index} for saving: ${error}`,
					originalRange: range
				});
				result.success = false;
			}
		});

		return result;
	}

	/**
	 * Handles timezone action clicks from UI components
	 */
	public handleTimezoneAction(action: string): void {
		switch (action) {
			case 'update-timezone':
				this._globalTimezoneService.resetToBrowserTimezone();
				break;
			case 'timezone-info':
				break;
			default:
				break;
		}
	}

	/**
	 * Round time to the nearest interval (e.g., 30 minutes)
	 * Minimal rounding to preserve accurate timezone conversions
	 */
	private roundTimeToInterval(timeStr: string, intervalMinutes: number, isEndTime: boolean = false): string {
		let [hours, minutes] = timeStr.split(':').map(Number);

		// RULE 1: Handle stored midnight representation (23:59 → 24:00)
		// This applies when 23:59 represents the stored format for midnight end times
		if (hours === 23 && minutes === 59 && isEndTime) {
			return '24:00';
		}

		// RULE 2: Only round times that are very close to interval boundaries
		// For 30-minute intervals, only round if within ±2 minutes of the boundary
		const distanceToLowerBoundary = minutes % intervalMinutes;
		const distanceToUpperBoundary = intervalMinutes - distanceToLowerBoundary;
		const shouldRound = distanceToLowerBoundary <= 2 || distanceToUpperBoundary <= 2;

		if (shouldRound) {
			// Round to nearest interval
			minutes = Math.round(minutes / intervalMinutes) * intervalMinutes;
			if (minutes === 60) {
				hours += 1;
				minutes = 0;
			}
		} else {
			// Preserve the exact converted time
		}

		// RULE 3: Handle 24:00+ overflow
		if (hours >= 24) {
			return '24:00';
		}

		return `${String(hours)}:${String(minutes).padStart(2, '0')}`;
	}

	/**
	 * Convert time string to minutes since midnight
	 */
	private timeToMinutes(timeStr: string): number {
		// Handle midnight case (24:00) - treat as 1440 minutes (24 * 60)
		if (timeStr === '24:00') {
			return 1440;
		}

		const [hours, minutes] = timeStr.split(':').map(Number);
		return hours * 60 + minutes;
	}

	/**
	 * Get day name from day index
	 */
	private getDayName(dayIndex: number): string {
		return new Intl.DateTimeFormat('en-US', { weekday: 'long' }).format(new Date(1970, 0, dayIndex + 4));
	}

	/**
	 * Merge adjacent time ranges within the same day to create a cleaner display
	 */
	private mergeTimeRanges(ranges: ConvertedRange[]): ConvertedRange[] {
		if (!ranges?.length) return [];

		// Group by day first
		const grouped = ranges.reduce(
			(acc, range) => {
				if (!acc[range.dayIndex]) {
					acc[range.dayIndex] = [];
				}
				acc[range.dayIndex].push(range);
				return acc;
			},
			{} as Record<number, ConvertedRange[]>
		);

		const mergedRanges: ConvertedRange[] = [];

		// Process each day separately
		Object.values(grouped).forEach((dayRanges) => {
			// Sort by start time
			const sorted = [...dayRanges].sort((a, b) => this.timeToMinutes(a.startTime) - this.timeToMinutes(b.startTime));

			if (sorted.length <= 1) {
				mergedRanges.push(...sorted);
				return;
			}

			let current = sorted[0];

			for (let i = 1; i < sorted.length; i++) {
				const next = sorted[i];

				// Check if ranges should be merged (adjacent or overlapping)
				if (this.shouldMergeTimeRanges(current, next)) {
					// Merge by extending the current range's end time
					const currentEndMinutes = this.timeToMinutes(current.endTime);
					const nextEndMinutes = this.timeToMinutes(next.endTime);

					current = {
						...current,
						endTime: currentEndMinutes >= nextEndMinutes ? current.endTime : next.endTime
					};
				} else {
					// Cannot merge, add current to results and move on
					mergedRanges.push(current);
					current = next;
				}
			}

			// Add the last range
			mergedRanges.push(current);
		});

		return mergedRanges;
	}

	/**
	 * Check if two time ranges should be merged
	 */
	private shouldMergeTimeRanges(a: ConvertedRange, b: ConvertedRange): boolean {
		// Same day check
		const isSameDay = a.dayIndex === b.dayIndex;

		// Adjacent or overlapping time check
		const aEndMinutes = this.timeToMinutes(a.endTime);
		const bStartMinutes = this.timeToMinutes(b.startTime);

		// Times are adjacent if end of first is the same as or just before start of second
		const isAdjacentTime = aEndMinutes >= bStartMinutes - 1;

		return isSameDay && isAdjacentTime;
	}

	/**
	 * Checks if two timezones are functionally equivalent (same offset/abbreviation)
	 * Used to determine if timezone banner should be shown
	 */
	private areTimezonesEquivalent(tz1: ITimeZone, tz2: ITimeZone): boolean {
		return timezonesAreFunctionallyEquivalent(tz1, tz2);
	}

	/**
	 * Merges availability ranges that are adjacent across day boundaries
	 * This handles cases where timezone conversion splits a single availability block
	 */
	/**
	 * Merges adjacent ranges only within the same day
	 * NEVER merges ranges across different days - each day should be isolated
	 */
	private mergeAdjacentDayRanges(ranges: any[]): any[] {
		if (!ranges || ranges.length <= 1) {
			return ranges;
		}

		// Sort ranges by day and start time
		const sorted = [...ranges].sort((a, b) => {
			if (a.dayIndex !== b.dayIndex) {
				return a.dayIndex - b.dayIndex;
			}
			return this.timeToMinutes(a.startTime) - this.timeToMinutes(b.startTime);
		});

		const merged: any[] = [];

		for (let i = 0; i < sorted.length; i++) {
			const current = sorted[i];
			const next = sorted[i + 1];

			// Only merge ranges within the SAME day that are touching/overlapping
			if (next && this.shouldMergeWithinSameDay(current, next)) {
				// Merge the ranges - use the start of first and end of second
				const mergedRange = {
					...current,
					startTime: current.startTime, // Keep earliest start
					endTime: next.endTime, // Keep latest end
					dayIndex: current.dayIndex // Same day for both
				};

				merged.push(mergedRange);
				i++; // Skip the next range since we merged it
			} else {
				merged.push(current);
			}
		}

		return merged;
	}

	/**
	 * Determines if two ranges should be merged within the same day only
	 * NEVER merges across different days - each day should remain isolated
	 */
	private shouldMergeWithinSameDay(first: any, second: any): boolean {
		// RULE 1: Only merge ranges on the same day
		if (first.dayIndex !== second.dayIndex) {
			return false; // Never merge across different days
		}

		// RULE 2: Only merge ranges from the same availability block
		const sameBlock = first.instructorAvailabilityBlockID === second.instructorAvailabilityBlockID;
		if (!sameBlock) {
			return false;
		}

		// RULE 3: Check if ranges are touching or overlapping (within 1 minute buffer)
		const firstEndMinutes = this.timeToMinutes(first.endTime);
		const secondStartMinutes = this.timeToMinutes(second.startTime);
		const bufferMinutes = 1; // Allow 1-minute buffer for touching ranges

		const areTouching = Math.abs(secondStartMinutes - firstEndMinutes) <= bufferMinutes;

		return areTouching;
	}

	/**
	 * Determines which day index to use for a merged range
	 */
	private getMajorityDayIndex(first: any, second: any): number {
		// For availability blocks, we should use the day where the block starts
		// This maintains the user's intent - if they scheduled something for "Monday evening"
		// it should appear as a Monday block even if it extends past midnight

		// Always use the start day (first range) for merged availability blocks
		return first.dayIndex;
	}

	/**
	 * Calculates day shift for saving conversion to preserve user intent
	 */
	private calculateDayShiftForSaving(originalTime: string, convertedTime: string, sourceTimezone: ITimeZone, targetTimezone: ITimeZone): number {
		const [originalHours] = originalTime.split(':').map(Number);
		const [convertedHours] = convertedTime.split(':').map(Number);

		// Calculate expected shift based on timezone difference
		const sourceOffset = getDSTAwareOffset(sourceTimezone);
		const targetOffset = getDSTAwareOffset(targetTimezone);
		const hourDifference = targetOffset - sourceOffset;

		// Determine if day boundary was crossed
		const adjustedHours = originalHours + hourDifference;

		if (adjustedHours < 0) {
			return -1; // Previous day
		} else if (adjustedHours >= 24) {
			return 1; // Next day
		}

		return 0; // Same day
	}

	/**
	 * Enhanced edge case handling methods for Phase 3
	 */

	/**
	 * Converts a single availability range with comprehensive validation
	 */
	private convertSingleRangeWithValidation(
		availability: any,
		displayTz: ITimeZone,
		instructorTz: ITimeZone,
		availableTimezones: ITimeZone[],
		index: number
	): ConversionResult {
		const result: ConversionResult = {
			success: true,
			ranges: [],
			errors: [],
			warnings: []
		};

		// Get source timezone
		const sourceTimezone = availableTimezones.find((tz) => tz.timeZoneID === availability.timezoneID) || instructorTz;

		// Check if conversion is needed
		if (sourceTimezone.timeZoneID === displayTz.timeZoneID) {
			result.ranges = [availability];
			return result;
		}

		// Validate time format
		if (!this.isValidTimeFormat(availability.startTime) || !this.isValidTimeFormat(availability.endTime)) {
			result.errors.push({
				type: 'invalid_time',
				message: `Invalid time format in range ${index}`,
				originalRange: availability
			});
			result.success = false;
			return result;
		}

		// Handle midnight representation
		const processedStartTime = this.handleMidnightRepresentation(availability.startTime, 'display');
		const processedEndTime = this.handleMidnightRepresentation(availability.endTime, 'display');

		// Convert times
		const convertedStartTime = convertTimeBetweenTimezones(processedStartTime, sourceTimezone, displayTz);
		const convertedEndTime = convertTimeBetweenTimezones(processedEndTime, sourceTimezone, displayTz);

		// Check for day boundary crossing
		const dayBoundaryCrossingResult = this.handleDayBoundaryCrossing(
			{
				dayIndex: availability.dayIndex,
				startTime: processedStartTime,
				endTime: processedEndTime,
				instructorAvailabilityBlockID: availability.instructorAvailabilityBlockID,
				timezoneID: availability.timezoneID
			},
			sourceTimezone,
			displayTz
		);

		result.ranges = dayBoundaryCrossingResult.ranges.map((range) => ({
			...availability,
			...range
		}));

		result.warnings.push(...dayBoundaryCrossingResult.warnings);

		// Check for DST transitions
		if (dayBoundaryCrossingResult.ranges.length === 1) {
			const dstResult = this.handleDSTTransitions(dayBoundaryCrossingResult.ranges[0], new Date());
			if (dstResult.adjusted) {
				result.warnings.push({
					type: 'time_adjustment',
					message: 'Time adjusted due to DST transition',
					originalRange: availability,
					adjustedRange: dstResult.range
				});
				result.ranges = [{ ...availability, ...dstResult.range }];
			}
		}

		return result;
	}

	/**
	 * Handles day boundary crossing during timezone conversion
	 */
	private handleDayBoundaryCrossing(range: Range, sourceTimezone: ITimeZone, targetTimezone: ITimeZone): { ranges: Range[]; warnings: ConversionWarning[] } {
		const warnings: ConversionWarning[] = [];

		// Convert times
		const convertedStartTime = convertTimeBetweenTimezones(range.startTime, sourceTimezone, targetTimezone);
		const convertedEndTime = convertTimeBetweenTimezones(range.endTime, sourceTimezone, targetTimezone);

		// Check for day changes
		const startDayIndex = convertDayBetweenTimezones(range.dayIndex, range.startTime, sourceTimezone, targetTimezone);
		const endDayIndex = convertDayBetweenTimezones(range.dayIndex, range.endTime, sourceTimezone, targetTimezone);

		if (startDayIndex === endDayIndex) {
			// No day crossing
			return {
				ranges: [
					{
						...range,
						dayIndex: startDayIndex,
						startTime: convertedStartTime,
						endTime: convertedEndTime
					}
				],
				warnings
			};
		}

		// Day crossing detected - split the range
		warnings.push({
			type: 'day_shift',
			message: `Availability split across days due to timezone conversion (${sourceTimezone.abbreviation} to ${targetTimezone.abbreviation})`,
			originalRange: range
		});

		const ranges: Range[] = [
			{
				...range,
				dayIndex: startDayIndex,
				startTime: convertedStartTime,
				endTime: '23:59'
			},
			{
				...range,
				dayIndex: endDayIndex,
				startTime: '00:00',
				endTime: convertedEndTime
			}
		];

		return { ranges, warnings };
	}

	/**
	 * Handles midnight representation conversion between display and storage formats
	 */
	private handleMidnightRepresentation(timeString: string, context: 'display' | 'storage'): string {
		if (context === 'display') {
			// Convert stored midnight (23:59) back to display midnight (24:00)
			if (timeString === '23:59' || timeString === '23:59:00') {
				return '24:00';
			}
		} else if (context === 'storage') {
			// Convert display midnight (24:00) to storage midnight (23:59)
			if (timeString === '24:00') {
				return '23:59:00';
			}
		}

		return timeString;
	}

	/**
	 * Handles DST transitions and time adjustments
	 */
	private handleDSTTransitions(range: Range, date: Date): { range: Range; adjusted: boolean } {
		// For now, return the range unchanged
		// In a full implementation, this would check for DST transition dates
		// and adjust times accordingly

		// TODO: Implement DST transition logic
		// - Check if the date falls on a DST transition day
		// - Adjust times that fall in the "lost hour" or "repeated hour"
		// - Handle edge cases like 2:30 AM on spring forward day

		return {
			range,
			adjusted: false
		};
	}

	/**
	 * Groups ranges for proper merging logic
	 */
	private groupRangesForMerging(ranges: any[]): Record<string, any[]> {
		return ranges.reduce(
			(groups, range) => {
				// Group by day and block ID to ensure proper merging
				const key = `${range.dayIndex}-${range.instructorAvailabilityBlockID || 'default'}`;
				if (!groups[key]) {
					groups[key] = [];
				}
				groups[key].push(range);
				return groups;
			},
			{} as Record<string, any[]>
		);
	}

	/**
	 * Merges a group of ranges with conflict detection
	 */
	private mergeRangeGroup(ranges: any[]): { ranges: any[]; conflicts: MergeConflict[]; mergedCount: number } {
		const conflicts: MergeConflict[] = [];
		let mergedCount = 0;

		if (ranges.length <= 1) {
			return { ranges, conflicts, mergedCount };
		}

		// Sort ranges by start time
		const sorted = [...ranges].sort((a, b) => this.timeToMinutes(a.startTime) - this.timeToMinutes(b.startTime));

		const merged: any[] = [];
		let current = sorted[0];

		for (let i = 1; i < sorted.length; i++) {
			const next = sorted[i];

			// Check for overlaps
			const currentEndMinutes = this.timeToMinutes(current.endTime);
			const nextStartMinutes = this.timeToMinutes(next.startTime);

			if (currentEndMinutes > nextStartMinutes) {
				// Overlap detected
				conflicts.push({
					type: 'overlap',
					message: `Overlapping time ranges detected: ${current.startTime}-${current.endTime} and ${next.startTime}-${next.endTime}`,
					conflictingRanges: [current, next]
				});
			}

			// Check if ranges should be merged
			if (this.shouldMergeTimeRanges(current, next)) {
				// Merge ranges
				current = {
					...current,
					endTime: currentEndMinutes >= this.timeToMinutes(next.endTime) ? current.endTime : next.endTime
				};
				mergedCount++;
			} else {
				// Gap between ranges - add current and continue
				merged.push(current);
				current = next;
			}
		}

		merged.push(current);

		return { ranges: merged, conflicts, mergedCount };
	}

	/**
	 * Converts a single range for saving with day preservation
	 */
	private convertRangeForSavingWithPreservation(
		range: TimeRange,
		displayTz: ITimeZone,
		instructorTz: ITimeZone,
		index: number
	): { range: TimeRange; success: boolean; errors: ConversionError[]; warnings: ConversionWarning[] } {
		const errors: ConversionError[] = [];
		const warnings: ConversionWarning[] = [];

		// Validate time format
		if (!this.isValidTimeFormat(range.startTime) || !this.isValidTimeFormat(range.endTime)) {
			errors.push({
				type: 'invalid_time',
				message: `Invalid time format in range ${index}`,
				originalRange: range
			});
			return { range, success: false, errors, warnings };
		}

		// Convert times
		const convertedStartTime = convertTimeBetweenTimezones(range.startTime, displayTz, instructorTz);
		const convertedEndTime = convertTimeBetweenTimezones(range.endTime, displayTz, instructorTz);

		// Apply day preservation logic
		const dayShift = this.calculateDayShiftForSaving(range.startTime, convertedStartTime, displayTz, instructorTz);
		let finalDayIndex = range.dayIndex;

		if (dayShift !== 0) {
			finalDayIndex = (range.dayIndex + dayShift + 7) % 7;
			warnings.push({
				type: 'day_shift',
				message: `Day shifted from ${this.getDayName(range.dayIndex)} to ${this.getDayName(finalDayIndex)} due to timezone conversion`,
				originalRange: range
			});
		}

		// Handle end time processing for storage
		const processedEndTime = this.handleMidnightRepresentation(convertedEndTime, 'storage');

		const convertedRange: TimeRange = {
			dayIndex: finalDayIndex,
			startTime: convertedStartTime,
			endTime: processedEndTime,
			dayName: this.getDayName(finalDayIndex)
		};

		return { range: convertedRange, success: true, errors, warnings };
	}

	/**
	 * Validates time format (HH:mm or HH:mm:ss)
	 */
	private isValidTimeFormat(timeStr: string): boolean {
		if (!timeStr) return false;

		// Allow 24:00 as valid midnight representation
		if (timeStr === '24:00') return true;

		// Check standard time format
		const timeRegex = /^([01]?[0-9]|2[0-3]):([0-5][0-9])(:([0-5][0-9]))?$/;
		return timeRegex.test(timeStr);
	}
}
